import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = signed out

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      setUser(null);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return data; // { twofa_required, challenge_id, email }
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    return data; // { twofa_required, challenge_id, email }
  }, []);

  const verifyOtp = useCallback(async (challenge_id, code) => {
    const { data } = await api.post("/auth/verify-otp", { challenge_id, code });
    if (data.token) localStorage.setItem("rwm_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("Logout request failed (clearing local session anyway):", e?.message); }
    localStorage.removeItem("rwm_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, verifyOtp, logout, refresh: loadMe }),
    [user, login, register, verifyOtp, logout, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
